import { useState, useEffect, useCallback } from "react";
import { useRouter, useParams, useSearchParams } from "next/navigation";
import Image from "next/image";
import { motion, useReducedMotion } from "framer-motion";
import { Gift, Info, Package, Truck } from "lucide-react";
import { cn } from "@/lib/utils";
import { Stat } from "@/components/ui/stat";
import { useAuthStore } from "@/stores/auth.store";
import { useToast } from "@/stores/ui.store";
import { useHaptics } from "@/hooks/useHaptics";
import { authFetch, isLoggedIn } from "@/lib/auth-client";
import { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AnimatedNumber } from "@/components/ui/AnimatedNumber";
import {
  ConfettiEffect,
  ParticleEffects,
  LEGENDARY_COLORS,
} from "@/components/pack-opening/ParticleEffects";
import {
  Modal,
  ModalContent,
  ModalTitle,
  ModalDescription,
} from "@/components/ui/modal";
import { ClaimPrizeModal } from "@/components/events/ClaimPrizeModal";
import { SurvivorPoolSheet } from "@/components/events/SurvivorPoolSheet";
import { FormatBadge, LengthBadge } from "@/components/events/EventBadges";
import { weekShortLabel } from "@/lib/week-label";
import { RoundStepper } from "@/components/events/RoundStepper";
import type { RoundStep } from "@/components/events/RoundStepper";
import {
  RosterSelectionModal,
  BestBallEntryError,
} from "@/components/events/prop-boost/RosterSelectionModal";
import {
  getTopPrizeDetail,
  PrizeArtwork,
} from "@/components/events/PrizeDisplay";
import type { Card } from "@/types";
import { refreshBalance } from "@/lib/balance";
import { scheduleMainEventNotifications } from "@/lib/notifications";
import type {
  MainEventDetail,
  MyEntryDetail,
  LeaderboardEntry,
  PrizeClaimData,
  SurvivorStatusData,
  SurvivorStandingRow,
  BestBallStandingRow,
  WeeklyScoreData,
} from "@/services/events/main-event";
import { WeekView, CAROUSEL_INSET } from "./_components/WeekView";
import { AboutSheet, LeaderboardSheet, PrizesSheet } from "./_components/sheets";
import { fmtPts } from "./_components/utils";
import { asset } from "@/lib/asset";

/**
 * ~1s 0→prize roll. Critically damped on purpose: any overshoot would tick the
 * coin total past the real award before settling back.
 */
const COUNT_UP_SPRING = { stiffness: 30, damping: 11 };
/** Long enough for the slowest confetti piece (≈4s fall) to leave the screen. */
const CELEBRATION_MS = 4500;

/**
 * The settled-event win moment.
 *
 * Coin prizes are credited server-side at settlement (main-event.service
 * addCoins), so there is deliberately nothing to collect here — a Collect
 * button would be a fake gesture. This reveals what was already awarded.
 *
 * The celebration (particles, haptic, count-up) fires only on the first view
 * of a given event's result, remembered per event in localStorage exactly like
 * the prize-cabinet auto-open above; every later visit renders the settled
 * state so reopening a finished event isn't a confetti cannon. It also fires
 * only when there is an actual prize — see `celebrateWin` below.
 */
function FinishBanner({
  eventId,
  rank,
  prize,
  totalScore,
}: {
  eventId: string;
  rank: number;
  prize: number;
  totalScore: number;
}) {
  const reducedMotion = useReducedMotion();
  const { fire } = useHaptics();

  // A pure read, so React's double-invoked initialiser is harmless; the write
  // that consumes the flag lives in the effect below. This banner only ever
  // first-renders on the client (event data arrives via fetch), so there is no
  // SSR markup for the storage-dependent branch to disagree with.
  const [firstView] = useState(() => {
    try {
      return !localStorage.getItem(`eventResultSeen:${eventId}`);
    } catch {
      // Storage blocked — replaying the moment beats never showing it.
      return true;
    }
  });

  const won = prize > 0;
  const celebrate = firstView && !reducedMotion;
  // Rank alone is NOT a win. prizeCoins is legitimately 0 for a top-3 finish
  // whenever the event paid nothing for that placement
  // — free events, or events that only pay 1st — so gating the burst on rank
  // fired a gold confetti cannon over the grey "you won nothing" banner. The
  // particles and the haptic are prize-gated; the banner itself still renders
  // for every rank, because "Scored 41 points" is a correct thing to show.
  const celebrateWin = celebrate && won && rank <= 3;
  const [coinValue, setCoinValue] = useState(celebrate ? 0 : prize);
  const [particlesOn, setParticlesOn] = useState(celebrateWin);

  useEffect(() => {
    try {
      localStorage.setItem(`eventResultSeen:${eventId}`, "1");
    } catch {
      /* no storage to remember with — the celebration may replay */
    }
  }, [eventId]);

  // Mount-time roll. AnimatedNumber initialises AT its first value, so the
  // count-up only happens when that first value was 0 (i.e. on a first view).
  useEffect(() => {
    setCoinValue(prize);
  }, [prize]);

  useEffect(() => {
    if (!celebrateWin) return;
    fire(rank === 1 ? "rare" : "success");
  }, [celebrateWin, rank, fire]);

  // Unmount the particle layers once they've played out — they don't self-clean.
  useEffect(() => {
    if (!particlesOn) return;
    const t = setTimeout(() => setParticlesOn(false), CELEBRATION_MS);
    return () => clearTimeout(t);
  }, [particlesOn]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-4">
      {particlesOn && !reducedMotion && (
        <>
          <ParticleEffects
            isActive
            intensity={rank === 1 ? "high" : "medium"}
            colors={LEGENDARY_COLORS}
          />
          {rank === 1 && <ConfettiEffect isActive />}
        </>
      )}
      <motion.div
        initial={reducedMotion ? false : { opacity: 0, y: 16, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={
          reducedMotion
            ? { duration: 0 }
            : { type: "spring", stiffness: 260, damping: 20 }
        }
        className={cn(
          "relative overflow-hidden rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4",
          won
            ? "bg-verdigris/10 border border-verdigris/30"
            : "bg-surface-2 border border-surface-3",
        )}
      >
        <div className="relative flex-shrink-0 self-start sm:self-auto">
          {won && (
            <motion.div
              aria-hidden
              className="absolute -inset-5 rounded-full"
              style={{
                background:
                  "radial-gradient(circle, rgba(184,160,76,0.55) 0%, rgba(184,160,76,0) 70%)",
              }}
              initial={reducedMotion ? false : { opacity: 0, scale: 0.8 }}
              animate={
                reducedMotion
                  ? { opacity: 0.5, scale: 1 }
                  : { opacity: [0, 0.9, 0.5], scale: [0.8, 1.15, 1] }
              }
              transition={
                reducedMotion
                  ? { duration: 0 }
                  : { duration: 1.1, ease: "easeOut", times: [0, 0.45, 1] }
              }
            />
          )}
          <Image
            src={asset("assets/icons/trophy.webp")}
            alt=""
            width={won ? 48 : 32}
            height={won ? 48 : 32}
            className="relative"
          />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className={cn(
              "font-serif font-bold text-lg",
              won ? "text-verdigris-text" : "text-cream",
            )}
          >
            You finished #{rank}!
          </p>
          {won ? (
            <>
              <div className="mt-1 flex items-baseline gap-2" aria-hidden>
                <Image
                  src={asset("assets/icons/coins.webp")}
                  alt=""
                  width={22}
                  height={22}
                  className="self-center"
                />
                <span className="font-serif font-bold text-3xl sm:text-4xl text-gold leading-none">
                  <AnimatedNumber
                    value={coinValue}
                    springConfig={COUNT_UP_SPRING}
                  />
                </span>
                <span className="text-sm text-warm-gray">coins</span>
              </div>
              <p className="text-sm text-warm-gray mt-1.5">
                {/* The visual amount rolls, so screen readers get the settled
                    sentence instead of a stream of intermediate numbers. */}
                <span className="sr-only">{prize.toLocaleString()} coins </span>
                added to your balance
              </p>
            </>
          ) : (
            <p className="text-sm text-warm-gray">Scored {totalScore} points</p>
          )}
        </div>
      </motion.div>
    </div>
  );
}

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  UPCOMING: { bg: "bg-blue-500/10 border-blue-500/30", text: "text-blue-400" },
  OPEN: { bg: "bg-verdigris/10 border-verdigris/30", text: "text-verdigris-text" },
  LOCKED: { bg: "bg-gold/10 border-gold/30", text: "text-gold" },
  SCORING: {
    bg: "bg-orange-500/10 border-orange-500/30",
    text: "text-orange-400",
  },
  COMPLETED: { bg: "bg-tertiary/10 border-tertiary/30", text: "text-tertiary" },
};

/**
 * Loading state for the event page. Mirrors the loaded layout (same max-w and
 * padding at every level) so the real content drops in without shifting: back
 * link, title, badge row, three stat tiles, the week rail, one game card.
 */
function EventSkeleton() {
  return (
    <div className="min-h-screen bg-surface" aria-busy="true" aria-label="Loading event">
      <div className="ornamental-rule max-w-7xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8 pb-4">
        {/* Back link + title */}
        <div className="mint-shimmer h-4 w-28 rounded mb-3" />
        <div className="mint-shimmer h-7 sm:h-8 w-3/4 max-w-sm rounded" />
        <div className="flex items-center gap-2 mt-2">
          <div className="mint-shimmer h-4 w-16 rounded" />
          <div className="mint-shimmer h-4 w-20 rounded" />
          <div className="mint-shimmer h-4 w-14 rounded" />
        </div>

        {/* Three stat tiles */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-4 sm:max-w-lg">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="min-w-0 rounded-xl bg-surface-2 border border-surface-3 px-3 py-2.5"
            >
              <div className="mint-shimmer h-2.5 w-12 rounded" />
              <div className="mint-shimmer h-5 w-16 rounded mt-1.5" />
              <div className="mint-shimmer h-2.5 w-10 rounded mt-1.5" />
            </div>
          ))}
        </div>
      </div>

      {/* Week rail */}
      <div className="bg-surface/95 border-b border-surface-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 sm:gap-3 py-2.5 sm:py-3 overflow-hidden">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="mint-shimmer w-28 h-12 rounded-lg flex-shrink-0" />
            ))}
          </div>
        </div>
      </div>

      {/* Countdown line + first carousel card. Deliberately byte-for-byte the
          same ghost WeekView renders in its own loading state (WeekView.tsx),
          so the page-skeleton → WeekView handoff swaps one block for an
          identical one instead of growing ~146px under the user. The inset is
          shared rather than copied — it decides the card's WIDTH as well as its
          position — but these heights are still hand-kept. */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-6">
        <div className="mb-6 h-5 w-40 rounded-md bg-surface-2 mint-shimmer" />
        <div className={cn('flex gap-4', CAROUSEL_INSET)}>
          <div className="shrink-0 w-[92%] max-w-[380px] h-56 rounded-xl bg-surface-2 mint-shimmer" />
        </div>
      </div>
    </div>
  );
}

export default function MainEventDetailPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const weekParam = searchParams.get("week");
  const { accessToken, user } = useAuthStore();
  const toast = useToast();
  const reducedMotion = useReducedMotion();
  const [event, setEvent] = useState<MainEventDetail | null>(null);
  const [myEntry, setMyEntry] = useState<MyEntryDetail | null>(null);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [entering, setEntering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Migrated from Next.js: original code awaited `params` (a Promise from
  // App Router) inside a useEffect. React Router exposes :id synchronously,
  // so we read it directly via useParams.
  const { id: eventIdParam } = useParams<{ id: string }>();
  const [eventId, setEventId] = useState<string>(eventIdParam ?? "");
  const [accessCode, setAccessCode] = useState("");
  const [myClaims, setMyClaims] = useState<PrizeClaimData[]>([]);
  const [claimModal, setClaimModal] = useState<PrizeClaimData | null>(null);
  // SURVIVOR format — weekly pick states for the week rail glyphs, plus the
  // standings list and "The Pool" sheet.
  const [survivorStatus, setSurvivorStatus] = useState<SurvivorStatusData | null>(null);
  const [survivorStandings, setSurvivorStandings] = useState<SurvivorStandingRow[]>([]);
  // Card-league formats (BEST_BALL / DRAFT_PPR) — season standings, per-week
  // points for the rail pills, and the roster-entry modal (Enter opens it
  // instead of POSTing).
  const [bestBallStandings, setBestBallStandings] = useState<BestBallStandingRow[]>([]);
  const [bbWeeklyMap, setBbWeeklyMap] = useState<Map<number, { points: number; isOfficial: boolean }>>(new Map());
  const [rosterModalOpen, setRosterModalOpen] = useState(false);
  const [userCards, setUserCards] = useState<Card[]>([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  // Header sheets — leaderboard/standings, pool, prizes, and About (hero + description).
  const [poolOpen, setPoolOpen] = useState(false);
  const [lbOpen, setLbOpen] = useState(false);
  const [prizesOpen, setPrizesOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  // The selected week (Undercard.round), mirrored to ?week=N.
  const [selectedRound, setSelectedRound] = useState<number | null>(null);
  // Live draft-pick counts reported by WeekView, overlaid on the rail dashes.
  const [draftCounts, setDraftCounts] = useState<Record<string, number>>({});
  // One-shot gold ring sweep over the header the moment an entry lands.
  const [entryRing, setEntryRing] = useState(false);

  // Prize cabinet auto-show: the first time a visitor lands on a prized event
  // they haven't entered, open the prizes sheet once (dismissible, remembered
  // per event). Entered users never see the auto-open.
  useEffect(() => {
    if (loading || !event || myEntry) return;
    if (event.prizePool <= 0 && (event.prizes?.length ?? 0) === 0) return;
    const seenKey = `prizeSheetSeen:${event.id}`;
    try {
      if (localStorage.getItem(seenKey)) return;
      localStorage.setItem(seenKey, "1");
    } catch {
      return;
    }
    setPrizesOpen(true);
  }, [loading, event, myEntry]);

  useEffect(() => {
    if (eventIdParam) setEventId(eventIdParam);
  }, [eventIdParam]);

  useEffect(() => {
    if (!eventId) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        // Reset per-event state so stale entry/claims (or a 404 on my-entry)
        // from a previously viewed event can't drive notifications or the week
        // rail for this one. (The SPA keeps this component mounted across
        // route-param changes.)
        setMyEntry(null);
        setMyClaims([]);
        setSurvivorStatus(null);
        setSurvivorStandings([]);
        setBestBallStandings([]);
        setBbWeeklyMap(new Map());
        setRosterModalOpen(false);
        setUserCards([]);
        setCardsLoading(false);
        setLeaving(false);
        setLeaderboard([]);
        setSelectedRound(null);
        setDraftCounts({});
        const eventRes = await fetch(`${API_BASE}/api/main-events/${eventId}`);

        // Format decides which leaderboard to fetch, so parse the event first.
        let survivorFormat = false;
        let cardLeagueFormat = false;
        if (eventRes.ok) {
          const eventData: MainEventDetail = await eventRes.json();
          survivorFormat = eventData.format === "SURVIVOR";
          cardLeagueFormat =
            eventData.format === "BEST_BALL" || eventData.format === "DRAFT_PPR";
          setEvent(eventData);
        } else setError("Event not found");

        // Survivor ranks by weeks survived, card leagues by season points,
        // standard events by pick points.
        const lbRes = cardLeagueFormat
          ? await fetch(
              `${API_BASE}/api/main-events/${eventId}/roster/standings?limit=100`,
            )
          : survivorFormat
            ? await fetch(
                `${API_BASE}/api/main-events/${eventId}/survivor/standings?limit=10`,
              )
            : await fetch(
                `${API_BASE}/api/main-events/${eventId}/leaderboard?limit=10`,
              );
        if (lbRes.ok) {
          if (cardLeagueFormat) setBestBallStandings(await lbRes.json());
          else if (survivorFormat) setSurvivorStandings(await lbRes.json());
          else setLeaderboard(await lbRes.json());
        }

        // Fetch user entry + prize claims if logged in
        if (isLoggedIn()) {
          const entryRes = await authFetch(
            `/api/main-events/${eventId}/my-entry`,
          );
          if (entryRes.ok) setMyEntry(await entryRes.json());

          const claimsRes = await authFetch(
            `/api/main-events/${eventId}/my-claims`,
          );
          if (claimsRes.ok) setMyClaims(await claimsRes.json());

          // Weekly pick states for the rail glyphs + WeekView's saved pick.
          if (survivorFormat) {
            const stRes = await authFetch(
              `/api/main-events/${eventId}/survivor/my-status`,
            );
            if (stRes.ok) setSurvivorStatus(await stRes.json());
          }

          // Card leagues: per-week points for the rail pills.
          if (cardLeagueFormat) {
            const wsRes = await authFetch(
              `/api/main-events/${eventId}/roster/weekly-scores`,
            );
            if (wsRes.ok) {
              const data = await wsRes.json();
              const map = new Map<number, { points: number; isOfficial: boolean }>();
              for (const w of (data.weeks ?? []) as WeeklyScoreData[]) {
                map.set(w.round, { points: w.optimalPoints, isOfficial: w.isOfficial });
              }
              setBbWeeklyMap(map);
            }
          }
        }
      } catch {
        setError("Failed to load event");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [eventId, accessToken]);

  // JestSDK notification scheduling — unchanged from the pre-restructure event
  // page. Waits for this event's data to load and confirms it matches the
  // route, otherwise stale entry/claims from a previously viewed event would
  // schedule notifications for the wrong one.
  useEffect(() => {
    if (!event || loading || event.id !== eventId) return;
    const sdk = typeof window !== "undefined" ? window.JestSDK : null;
    // Ledger truth, not a placement lookup: a tied winner is credited a split
    // of the pooled ranks, which a placement lookup can report as zero — and
    // that would silently withhold the "you won" notification from someone who
    // actually got paid.
    const wonPrize =
      event.status === "COMPLETED" && !!myEntry?.rank && (myEntry.prizeCoins ?? 0) > 0;

    // Per-undercard "have they made picks yet?" map — feeds the progress-
    // based rescheduling so we don't badger users who already locked their
    // selections. An undercard with picksSubmitted > 0 counts as "done".
    const picksByUndercard: Record<string, boolean> = {};
    for (const uc of myEntry?.undercardEntries ?? []) {
      picksByUndercard[uc.undercardId] = uc.picksSubmitted > 0;
    }

    void scheduleMainEventNotifications(sdk, event, {
      hasEntry: !!myEntry,
      wonPrize,
      unclaimedPrize: myClaims.some((claim) => claim.status === "UNCLAIMED"),
      picksByUndercard,
    });
  }, [event, myEntry, myClaims, loading, eventId]);

  // Resolve the selected week: a valid ?week=N wins; otherwise the next open
  // week (earliest UPCOMING/OPEN), falling back to the latest week. The URL is
  // updated to reflect the resolution. Re-runs on back/forward (weekParam).
  useEffect(() => {
    if (!event || event.id !== eventId || event.undercards.length === 0) return;
    const sorted = [...event.undercards].sort((a, b) => a.round - b.round);
    const parsed = weekParam != null ? parseInt(weekParam, 10) : NaN;
    let target: number;
    if (!Number.isNaN(parsed) && sorted.some((u) => u.round === parsed)) {
      target = parsed;
    } else {
      const nextOpen = sorted.find(
        (u) => u.status === "UPCOMING" || u.status === "OPEN",
      );
      target = (nextOpen ?? sorted[sorted.length - 1]).round;
    }
    setSelectedRound(target);
    if (String(target) !== weekParam) {
      router.replace(`/events/main/${eventId}?week=${target}`);
    }
  }, [event, weekParam, eventId, router]);

  // Pill tap — instant local switch, URL kept in sync without a full
  // navigation (react-router replace on the same route keeps this mounted).
  const selectWeek = useCallback(
    (round: number) => {
      setSelectedRound(round);
      router.replace(`/events/main/${eventId}?week=${round}`);
    },
    [eventId, router],
  );

  // Refetch my-entry + claims (used by WeekView after enter/save actions).
  const refreshEntry = useCallback(async () => {
    if (!isLoggedIn() || !eventId) return;
    const entryRes = await authFetch(`/api/main-events/${eventId}/my-entry`);
    if (entryRes.ok) setMyEntry(await entryRes.json());
    const claimsRes = await authFetch(`/api/main-events/${eventId}/my-claims`);
    if (claimsRes.ok) setMyClaims(await claimsRes.json());
  }, [eventId]);

  const handleJoinableChange = useCallback((joinable: boolean) => {
    setEvent((prev) => (prev ? { ...prev, joinable } : prev));
  }, []);

  // Best Ball: refresh standings + rail points + event header counts after
  // an enter/leave mutation.
  const refreshBestBall = useCallback(async () => {
    const [lbRes, evRes] = await Promise.all([
      fetch(`${API_BASE}/api/main-events/${eventId}/roster/standings?limit=100`),
      fetch(`${API_BASE}/api/main-events/${eventId}`),
    ]);
    if (lbRes.ok) setBestBallStandings(await lbRes.json());
    if (evRes.ok) setEvent(await evRes.json());
    if (isLoggedIn()) {
      const wsRes = await authFetch(`/api/main-events/${eventId}/roster/weekly-scores`);
      if (wsRes.ok) {
        const data = await wsRes.json();
        const map = new Map<number, { points: number; isOfficial: boolean }>();
        for (const w of (data.weeks ?? []) as WeeklyScoreData[]) {
          map.set(w.round, { points: w.optimalPoints, isOfficial: w.isOfficial });
        }
        setBbWeeklyMap(map);
      }
    }
  }, [eventId]);

  // Best Ball entry — Enter opens the roster builder (cards fetched lazily).
  const openRosterModal = useCallback(async () => {
    if (!isLoggedIn()) {
      router.push("/login");
      return;
    }
    setRosterModalOpen(true);
    setCardsLoading(true);
    try {
      const res = await authFetch("/api/cards");
      if (res.ok) {
        const data = await res.json();
        setUserCards(
          ((data.cards ?? []) as (Card & { sport?: string })[]).filter(
            (c) => c.sport === "NFL",
          ),
        );
      }
    } finally {
      setCardsLoading(false);
    }
  }, [router]);

  /**
   * Entry landed. Success toast (the store auto-fires the success haptic) plus
   * a one-shot gold ring sweep on the header. Coin motion is deliberately absent
   * — refreshBalance() + AnimatedDelta already animate the nav balance.
   */
  const celebrateEntry = useCallback(
    (title?: string) => {
      toast.success("You're in!", title);
      if (!reducedMotion) setEntryRing(true);
    },
    [toast, reducedMotion],
  );

  /**
   * WeekView's action-bar entry landed (Enter Event / Enter & Save). That path
   * owns its own POST, so the celebration has to be handed down — without this
   * the most-used entry button was the only one that never fired the win
   * moment. Same copy + gold ring as handleEnter / handleBestBallConfirm.
   */
  const handleWeekViewEntered = useCallback(() => {
    // Same optimistic bump handleEnter does — the action-bar enter path would
    // otherwise leave the Entered tile stale until a full reload.
    setEvent((prev) => (prev ? { ...prev, entryCount: prev.entryCount + 1 } : prev));
    celebrateEntry(event?.title);
  }, [celebrateEntry, event?.title]);

  // POST the 10 cardIds; structured failures re-throw as BestBallEntryError so
  // the modal renders code-specific copy (gates, shape, slot conflicts…).
  const handleBestBallConfirm = useCallback(
    async (cardIds: string[]) => {
      const res = await authFetch(`/api/main-events/${eventId}/enter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cardIds }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new BestBallEntryError(data);
      setRosterModalOpen(false);
      void refreshBalance();
      celebrateEntry(event?.title);
      await refreshEntry();
      void refreshBestBall();
    },
    [eventId, refreshEntry, refreshBestBall, celebrateEntry, event?.title],
  );

  // Leave with full refund — only offered while the league is still joinable.
  // The in-app confirm modal (leaveConfirmOpen) gates this; no native confirm().
  const handleLeaveLeague = useCallback(async () => {
    setLeaveConfirmOpen(false);
    try {
      setLeaving(true);
      setError(null);
      const res = await authFetch(`/api/main-events/${eventId}/leave`, { method: "POST" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          data.code === "BEST_BALL_LEAVE_CLOSED"
            ? "The season has kicked off — rosters are locked in."
            : data.error || "Failed to leave league",
        );
      }
      setMyEntry(null);
      setBbWeeklyMap(new Map());
      void refreshBalance();
      void refreshBestBall();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to leave league");
    } finally {
      setLeaving(false);
    }
  }, [eventId, refreshBestBall]);

  const handleDraftCountChange = useCallback((ucId: string, count: number) => {
    setDraftCounts((prev) => (prev[ucId] === count ? prev : { ...prev, [ucId]: count }));
  }, []);

  const handleEnter = async () => {
    if (!isLoggedIn()) {
      router.push("/login");
      return;
    }
    // Card leagues (Best Ball / Draft PPR) enter via the roster builder, not a
    // bare POST.
    if (event?.format === "BEST_BALL" || event?.format === "DRAFT_PPR") {
      void openRosterModal();
      return;
    }
    try {
      setEntering(true);
      setError(null);
      const body =
        event?.accessType === "CODE_REQUIRED" ? { accessCode } : undefined;
      const res = await authFetch(`/api/main-events/${eventId}/enter`, {
        method: "POST",
        ...(body
          ? {
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(body),
            }
          : {}),
      });
      const data = await res.json();
      if (!res.ok) {
        // Pool closed at Week 1 kickoff while the page sat open on a stale
        // joinable snapshot — flip to the Entry closed chip so Enter can't retry.
        if (data.code === "SURVIVOR_JOIN_CLOSED") {
          setEvent((prev) => (prev ? { ...prev, joinable: false } : prev));
          throw new Error("Pool locked — Week 1 has started.");
        }
        throw new Error(data.error || "Failed to enter");
      }
      // Refresh entry (+ balance for paid entries, + survivor status so the
      // rail/banner reflect the new entry). Entered-count bumps optimistically
      // — the event payload isn't refetched here, so the tile would otherwise
      // show the stale count until the next full load.
      setEvent((prev) =>
        prev ? { ...prev, entryCount: prev.entryCount + 1 } : prev,
      );
      void refreshBalance();
      celebrateEntry(event?.title);
      await refreshEntry();
      if (event?.format === "SURVIVOR") {
        const stRes = await authFetch(
          `/api/main-events/${eventId}/survivor/my-status`,
        );
        if (stRes.ok) setSurvivorStatus(await stRes.json());
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to enter");
    } finally {
      setEntering(false);
    }
  };

  if (loading) return <EventSkeleton />;

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-warm-gray">Event not found</p>
      </div>
    );
  }

  const isJoinable = event.status === "UPCOMING" || event.status === "OPEN";
  const isSurvivor = event.format === "SURVIVOR";
  const isBestBall = event.format === "BEST_BALL";
  const isDraftPpr = event.format === "DRAFT_PPR";
  // Card leagues share the entire roster/standings/lineup surface; isDraftPpr
  // remains only where behavior actually differs (the weekly lineup editor).
  const isCardLeague = isBestBall || isDraftPpr;
  // Same condition as the header's Enter control. A CODE_REQUIRED event is
  // excluded: its code input lives on the page, so offering Enter from inside
  // the sheet would submit an empty code.
  const canEnterFromSheet =
    !myEntry
    && !event.isFull
    && isJoinable
    && ((!isSurvivor && !isCardLeague) || event.joinable)
    && !(event.accessType === "CODE_REQUIRED" && !isCardLeague);
  const statusStyle = STATUS_COLORS[event.status] || STATUS_COLORS.UPCOMING;

  const sortedUndercards = [...event.undercards].sort((a, b) => a.round - b.round);
  const selectedUndercard =
    sortedUndercards.find((u) => u.round === selectedRound) ?? null;

  // Week rail pills — "Wk N" + status dot; survivor glyphs, best-ball weekly
  // points, or pick-progress dashes / "✓ points" (completed) for standard.
  const savedPickCounts = new Map(
    (myEntry?.undercardEntries ?? []).map((ue) => [ue.undercardId, ue.picksSubmitted ?? 0]),
  );
  const railRounds: RoundStep[] = sortedUndercards.map((uc) => ({
    id: uc.id,
    title: weekShortLabel(uc.round, uc.title),
    status: uc.status,
    round: uc.round,
    gameCount: isSurvivor || isCardLeague ? undefined : uc.gameCount,
    picksMade:
      isSurvivor || isCardLeague
        ? undefined
        : (draftCounts[uc.id] ?? savedPickCounts.get(uc.id) ?? 0),
    completedPoints:
      !isSurvivor && !isCardLeague && myEntry && uc.status === "COMPLETED"
        ? (myEntry.undercardEntries.find((ue) => ue.undercardId === uc.id)
            ?.totalScore ?? null)
        : null,
    bestBallWeek: isCardLeague ? (bbWeeklyMap.get(uc.round) ?? null) : null,
  }));

  // Card-league header stat — season points + rank (matched in the standings).
  const myBestBallRank = isCardLeague && user
    ? (bestBallStandings.find((r) => r.userId === user.id)?.rank ?? null)
    : null;

  // Header stat A — score/status; opens the leaderboard (standings) sheet.
  const survivorEntryStat = myEntry?.survivor
    ? myEntry.survivor.status === "ALIVE"
      ? { value: "ALIVE", valueClass: "text-verdigris-text", sub: `${myEntry.survivor.weeksSurvived} wks`, subClass: "text-verdigris-text font-bold" }
      : { value: "OUT", valueClass: "text-oxblood-text", sub: weekShortLabel(myEntry.survivor.eliminatedWeek), subClass: "text-oxblood-text font-bold" }
    : null;

  const prizeStatValue =
    event.prizePool > 0
      ? event.prizePool.toLocaleString()
      : event.prizes.length > 0
        ? "View"
        : "—";
  const hasPrizes = event.prizePool > 0 || event.prizes.length > 0;
  const topPrize = getTopPrizeDetail(event);

  return (
    <div className="min-h-screen bg-surface">
      <div className="ornamental-rule max-w-7xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8" />

      {/* Compressed event header */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8 pb-4">
        {/* Entry landed — one gold ring sweep, then it unmounts itself. */}
        {entryRing && (
          <motion.div
            className="absolute inset-0 rounded-xl border-2 border-gold pointer-events-none"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: [0, 0.8, 0], scale: [0.98, 1.02] }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            onAnimationComplete={() => setEntryRing(false)}
          />
        )}
        <button
          onClick={() => router.push("/events")}
          className="text-sm text-tertiary hover:text-cream mb-3 inline-block"
        >
          ← Back to Events
        </button>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="min-w-0">
            {/* Hero art + long description live behind the title tap (About sheet) */}
            <button
              type="button"
              onClick={() => setAboutOpen(true)}
              className="group text-left"
              aria-label={`About ${event.title}`}
            >
              <h1 className="text-xl sm:text-2xl font-serif font-bold text-cream break-words">
                {event.title}
                <Info className="inline-block w-4 h-4 ml-2 align-baseline text-tertiary group-hover:text-gold transition-colors" />
              </h1>
            </button>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span
                className={cn(
                  "px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border shrink-0",
                  statusStyle.bg,
                  statusStyle.text,
                )}
              >
                {event.status}
              </span>
              <FormatBadge format={event.format} />
              <LengthBadge startTime={event.startTime} endTime={event.endTime} durationClass={event.durationClass} />
              {event.accessType === "CODE_REQUIRED" && (
                <span className="text-gold text-xs">Code required</span>
              )}
            </div>
          </div>

          {/* Full events: entry closed; point at the auto-respawned successor. */}
          {!myEntry && event.isFull && (
            <div className="text-left sm:text-right shrink-0">
              <span className="inline-block px-3 py-1.5 rounded-lg border border-oxblood/40 bg-oxblood/10 text-sm font-bold text-oxblood-text">
                Event full{event.maxEntries != null ? ` — ${event.entryCount}/${event.maxEntries}` : ''}
              </span>
              {event.successor && (
                <button
                  type="button"
                  onClick={() => router.push(`/events/main/${event.successor!.id}`)}
                  className="mt-1 block w-full text-left sm:text-right text-xs font-bold text-gold hover:text-gold-hover underline underline-offset-2"
                >
                  Join {event.successor.title} →
                </button>
              )}
            </div>
          )}
          {/* Enter controls stay visible in the header when not entered */}
          {!myEntry && !event.isFull && isJoinable && ((!isSurvivor && !isCardLeague) || event.joinable) && (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto shrink-0">
              {event.accessType === "CODE_REQUIRED" && !isCardLeague && (
                <input
                  type="text"
                  placeholder="Enter access code"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                  className="px-3 py-2 rounded-lg bg-surface-2 border border-surface-3 text-cream placeholder-warm-gray/50 focus:border-gold focus:outline-none font-mono text-sm w-full sm:w-48"
                />
              )}
              <Button
                variant="coins"
                size="lg"
                onClick={handleEnter}
                disabled={
                  entering ||
                  (event.accessType === "CODE_REQUIRED" && !isCardLeague && !accessCode.trim())
                }
                className="w-full max-w-sm mx-auto sm:mx-0 sm:w-auto"
              >
                {entering
                  ? "Entering..."
                  : isCardLeague
                    ? event.entryFee === 0
                      ? "Build Lineup & Enter"
                      : `Build Lineup (${event.entryFee} coins)`
                    : event.entryFee === 0
                      ? "Enter Event"
                      : `Enter (${event.entryFee} coins)`}
              </Button>
            </div>
          )}
          {/* Survivor pools / card leagues lock at the first kickoff */}
          {!myEntry && (isSurvivor || isCardLeague) && !event.joinable && (
            <div className="text-left sm:text-right shrink-0">
              <span className="inline-block px-3 py-1.5 rounded-lg border border-surface-3 bg-surface-2 text-sm font-bold text-warm-gray/70 cursor-not-allowed">
                Entry closed
              </span>
              <p className="text-xs text-warm-gray mt-1">
                {isCardLeague
                  ? "League locked — the season has kicked off."
                  : "Pool locked — Week 1 has started."}
              </p>
            </div>
          )}
          {/* Card leagues: leave with full refund while the league is joinable */}
          {myEntry && isCardLeague && event.joinable && (
            <div className="text-left sm:text-right shrink-0">
              <button
                type="button"
                onClick={() => setLeaveConfirmOpen(true)}
                disabled={leaving}
                className="text-xs text-warm-gray hover:text-oxblood-text transition-colors underline underline-offset-2 disabled:opacity-50"
              >
                {leaving ? "Leaving…" : "Leave league — full refund"}
              </button>
              <p className="text-[11px] text-tertiary mt-1">
                Until the first kickoff
              </p>
            </div>
          )}
        </div>

        {/* Three tappable stats — leaderboard, pool/entries, prizes */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-4 sm:max-w-lg">
          {isCardLeague ? (
            <Stat
              label={myEntry?.rosterEntry ? "Your Points" : "Standings"}
              value={myEntry?.rosterEntry ? fmtPts(myEntry.rosterEntry.seasonPoints) : "—"}
              sub={myBestBallRank ? `Rank #${myBestBallRank}` : "View"}
              onClick={() => setLbOpen(true)}
            />
          ) : isSurvivor ? (
            <Stat
              label={survivorEntryStat ? "Your Status" : "Standings"}
              value={survivorEntryStat?.value ?? "—"}
              sub={survivorEntryStat?.sub ?? "View"}
              subClass={survivorEntryStat?.subClass}
              valueClass={survivorEntryStat?.valueClass}
              onClick={() => setLbOpen(true)}
            />
          ) : (
            <Stat
              label={myEntry ? "Your Score" : "Leaderboard"}
              value={myEntry ? myEntry.totalScore : "—"}
              sub={myEntry?.rank ? `Rank #${myEntry.rank}` : "View"}
              onClick={() => setLbOpen(true)}
            />
          )}
          {isSurvivor && event.survivor ? (
            <Stat
              label="Alive"
              value={
                <>
                  {event.survivor.aliveCount}
                  <span className="text-sm text-warm-gray">/{event.survivor.totalCount}</span>
                </>
              }
              sub="The Pool"
              onClick={() => setPoolOpen(true)}
            />
          ) : (
            <Stat label="Entered" value={event.entryCount} />
          )}
          <Stat
            label={topPrize?.type === "ITEM" ? "Top Prize" : "Prize Pool"}
            value={
              topPrize?.type === "ITEM" ? (
                <span className="flex items-center gap-1.5">
                  <PrizeArtwork
                    prize={topPrize.prize}
                    coinIconSrc={asset("assets/icons/coins.webp")}
                    className="h-6 w-6 shrink-0 rounded border border-gold/40"
                    iconClassName="h-3.5 w-3.5"
                  />
                  <span className="truncate text-sm">
                    {topPrize.prize.itemName ?? "Mystery Prize"}
                  </span>
                </span>
              ) : (
                prizeStatValue
              )
            }
            sub={hasPrizes ? "All prizes" : undefined}
            onClick={hasPrizes ? () => setPrizesOpen(true) : undefined}
          />
        </div>
      </div>

      {/* Winner/Placement Banner — on a settled event the result outranks the
          picks, so it sits above the week rail rather than under them. */}
      {event.status === "COMPLETED" && myEntry && myEntry.rank && (
        <FinishBanner
          // Keyed: this page stays mounted across route-param changes, so the
          // banner must remount (and re-run its first-view check) per event.
          key={event.id}
          eventId={event.id}
          rank={myEntry.rank}
          prize={myEntry.prizeCoins}
          totalScore={myEntry.totalScore}
        />
      )}

      {/* Sticky week rail */}
      {sortedUndercards.length > 1 && (
        <RoundStepper
          rounds={railRounds}
          currentRoundId={selectedUndercard?.id}
          eventId={eventId}
          onNavigate={(id) => {
            const uc = sortedUndercards.find((u) => u.id === id);
            if (uc) selectWeek(uc.round);
          }}
          survivorPicks={
            isSurvivor
              ? new Map(
                  (survivorStatus?.picksByWeek ?? []).map((p) => [
                    p.round,
                    { team: p.team, result: p.result },
                  ]),
                )
              : undefined
          }
          eliminatedWeek={
            isSurvivor ? (survivorStatus?.eliminatedWeek ?? null) : undefined
          }
        />
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-6">
        {error && (
          <div className="p-4 mb-6 bg-oxblood/10 border border-oxblood/30 rounded-xl text-oxblood-text text-sm">
            {error}
          </div>
        )}

        {/* The selected week's picks — the event page IS the picks page.
            Deliberately NOT keyed/wrapped per week: WeekView owns the week
            cache and the unsaved draft map in refs + state, so remounting it on
            every week switch would throw both away. The week crossfade lives
            inside WeekView, over its content region only. */}
        {selectedUndercard && (
          <WeekView
            eventId={eventId}
            undercardId={selectedUndercard.id}
            isSurvivor={isSurvivor}
            isBestBall={isBestBall}
            isDraftPpr={isDraftPpr}
            sport={event.sport}
            accessType={event.accessType}
            entryFee={event.entryFee}
            joinable={event.joinable}
            onJoinableChange={handleJoinableChange}
            hasEntered={!!myEntry}
            myEntry={myEntry}
            refreshEntry={refreshEntry}
            survivorStatus={survivorStatus}
            onSurvivorStatusChange={setSurvivorStatus}
            onDraftCountChange={handleDraftCountChange}
            onEntered={handleWeekViewEntered}
          />
        )}
        {sortedUndercards.length === 0 && (
          <p className="text-tertiary py-12 text-center">No rounds scheduled yet.</p>
        )}

        {/* Item Prize Claims — kept on-page (below the picks) so an actionable
            claim is never buried inside a sheet */}
        {myClaims.length > 0 && (
          <div className="mt-6 space-y-4">
            {myClaims.map((claim) => (
              <div
                key={claim.id}
                className={cn(
                  "p-4 sm:p-5 rounded-xl flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4",
                  claim.status === "UNCLAIMED"
                    ? "bg-gold/10 border border-gold/30"
                    : claim.status === "SHIPPED"
                      ? "bg-verdigris/10 border border-verdigris/30"
                      : "bg-surface-2 border border-surface-3",
                )}
              >
                {claim.itemImageUrl ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={claim.itemImageUrl}
                    alt=""
                    className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                  />
                ) : (
                  <Gift className="w-8 h-8 text-gold flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-serif font-bold text-cream break-words">
                    {claim.status === "UNCLAIMED"
                      ? `You won: ${claim.itemName}!`
                      : claim.itemName}
                  </p>
                  <p className="text-sm text-warm-gray flex items-center gap-1">
                    {claim.status === "UNCLAIMED" &&
                      "Claim your prize by submitting shipping info"}
                    {claim.status === "CLAIMED" && (
                      <>
                        <Package className="w-3.5 h-3.5 text-gold" /> Processing —
                        we&apos;ll notify you when it ships
                      </>
                    )}
                    {claim.status === "SHIPPED" && (
                      <>
                        <Truck className="w-3.5 h-3.5 text-verdigris-text" /> Shipped
                        {claim.trackingNumber ? ` — ${claim.trackingNumber}` : ""}
                      </>
                    )}
                  </p>
                </div>
                {claim.status === "UNCLAIMED" && (
                  <Button
                    variant="coins"
                    className="w-full sm:w-auto shrink-0"
                    onClick={() => setClaimModal(claim)}
                  >
                    Claim Prize
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Claim Prize Modal */}
        {/* Leave-league confirm — replaces the native window.confirm() */}
        <Modal open={leaveConfirmOpen} onOpenChange={setLeaveConfirmOpen}>
          <ModalContent className="max-w-sm">
            <ModalTitle>Leave this league?</ModalTitle>
            <ModalDescription className="mt-2">
              Your roster unlocks and the entry fee is refunded in full. You can
              re-enter any time before the first kickoff.
            </ModalDescription>
            <div className="mt-5 flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setLeaveConfirmOpen(false)}>
                Stay
              </Button>
              <Button
                variant="outline"
                onClick={handleLeaveLeague}
                disabled={leaving}
                className="border-oxblood/40 text-oxblood-text hover:bg-oxblood/10"
              >
                {leaving ? "Leaving…" : "Leave & refund"}
              </Button>
            </div>
          </ModalContent>
        </Modal>

        <ClaimPrizeModal
          claim={
            claimModal
              ? {
                  id: claimModal.id,
                  mainEventId: claimModal.mainEventId,
                  itemName: claimModal.itemName,
                  itemImageUrl: claimModal.itemImageUrl,
                  rank: claimModal.rank,
                }
              : null
          }
          userEmail={useAuthStore.getState().user?.email || ""}
          isOpen={!!claimModal}
          onClose={() => setClaimModal(null)}
          onClaimed={async () => {
            const res = await authFetch(
              `/api/main-events/${eventId}/my-claims`,
            );
            if (res.ok) setMyClaims(await res.json());
          }}
        />

        {/* Header-stat sheets */}
        <AboutSheet open={aboutOpen} onClose={() => setAboutOpen(false)} event={event} />
        <LeaderboardSheet
          open={lbOpen}
          onClose={() => setLbOpen(false)}
          isSurvivor={isSurvivor}
          isBestBall={isCardLeague}
          leaderboard={leaderboard}
          survivorStandings={survivorStandings}
          bestBallStandings={bestBallStandings}
          myUserId={user?.id ?? null}
        />
        {/* The Enter action mirrors the header button's gate exactly — the
            sheet must never offer a route into an event the page itself says
            is closed, full, or already entered. A CODE_REQUIRED event still
            needs the code field on the page, so the sheet only closes and
            lets the header take over. */}
        <PrizesSheet
          open={prizesOpen}
          onClose={() => setPrizesOpen(false)}
          event={event}
          onEnter={canEnterFromSheet ? handleEnter : undefined}
          enterLabel={
            isCardLeague
              ? event.entryFee === 0
                ? "Build Lineup & Enter"
                : `Build Lineup (${event.entryFee} coins)`
              : event.entryFee === 0
                ? "Enter Event"
                : `Enter (${event.entryFee} coins)`
          }
          entering={entering}
        />

        {/* "The Pool" attrition sheet — opened from the Alive header stat */}
        {isSurvivor && (
          <SurvivorPoolSheet
            mainEventId={eventId}
            open={poolOpen}
            onClose={() => setPoolOpen(false)}
          />
        )}

        {/* Card-league roster builder — Enter opens this; POSTs { cardIds }.
            Mounted whenever the format is a card league (config falls back to
            defaults) so the Enter button can never silently no-op. */}
        {isCardLeague && (
          <RosterSelectionModal
            isOpen={rosterModalOpen}
            onClose={() => setRosterModalOpen(false)}
            onConfirm={(cardIds) => handleBestBallConfirm(cardIds)}
            cards={userCards}
            rosterSize={event.bestBall?.rosterSize ?? 10}
            sport="NFL"
            bestBall={{
              gates: event.bestBall?.gates ?? { minRare: 0, minLegendary: 0 },
              durationClass: event.bestBall?.durationClass ?? null,
              format: isDraftPpr ? "DRAFT_PPR" : "BEST_BALL",
            }}
            loading={cardsLoading}
            entryFee={event.entryFee}
            entryFeeType={event.entryFee > 0 ? "COINS" : "FREE"}
            userBalance={user?.coins ?? 0}
          />
        )}
      </main>
    </div>
  );
}
